import { alpha, Box, Stack, Typography, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { CustomAvatar } from 'src/components/custom-avatar';
import Image from 'src/components/image';
import { VisitTranscriptionSpeaker } from 'src/types/visit-transcription';
import type { VisitTranscriptionSpeakerValue } from 'src/types/visit-transcription';

const appear = keyframes`
  from { opacity: 0; transform: scale(0.985); }
  to { opacity: 1; transform: scale(1); }
`;

export interface SpeakerParty {
  name?: string;
  avatar?: string;
}

// App-wide placeholder avatar (same asset used across the dashboard for empty avatars).
const PLACEHOLDER_AVATAR = '/assets/avatars/avatar.png';

const SPEAKER_LABELS: Record<VisitTranscriptionSpeakerValue, string> = {
  [VisitTranscriptionSpeaker.Professional]: 'Specjalista',
  [VisitTranscriptionSpeaker.Caregiver]: 'Opiekun',
  [VisitTranscriptionSpeaker.Unknown]: 'Nieznany',
};

export { SPEAKER_LABELS };

interface ResolvedSpeakerStyle {
  align: 'flex-start' | 'flex-end';
  bubbleBg: string;
  label: string;
  party?: SpeakerParty;
  isAssistant: boolean;
}

export const resolveSpeakerStyle = (
  speaker: VisitTranscriptionSpeakerValue,
  professional?: SpeakerParty,
  caregiver?: SpeakerParty,
  // Whose viewpoint we render from — "me" gets the right side + primary colour.
  // Defaults to the professional (the auth-side app). The guest/owner view passes
  // 'caregiver' so the owner reads on the right (WhatsApp-style).
  perspective: 'professional' | 'caregiver' = 'professional',
): ResolvedSpeakerStyle => {
  const caregiverIsMe = perspective === 'caregiver';
  if (speaker === VisitTranscriptionSpeaker.Professional) {
    return {
      align: caregiverIsMe ? 'flex-start' : 'flex-end',
      bubbleBg: caregiverIsMe ? 'grey.100' : 'primary.lighter',
      label: professional?.name?.trim() || SPEAKER_LABELS[speaker],
      party: professional,
      isAssistant: false,
    };
  }
  if (speaker === VisitTranscriptionSpeaker.Caregiver) {
    return {
      align: caregiverIsMe ? 'flex-end' : 'flex-start',
      bubbleBg: caregiverIsMe ? 'primary.lighter' : 'grey.100',
      label: caregiver?.name?.trim() || SPEAKER_LABELS[speaker],
      party: caregiver,
      isAssistant: false,
    };
  }
  return {
    align: 'flex-start',
    bubbleBg: 'background.neutral',
    label: SPEAKER_LABELS[speaker],
    isAssistant: true,
  };
};

export const AssistantAvatar = () => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      flexShrink: 0,
      backgroundColor: '#052330',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Image src="/assets/logo/logo_symbol.svg" sx={{ width: 20, height: 20 }} />
  </Box>
);

interface TranscriptChatRowProps {
  speaker: VisitTranscriptionSpeakerValue;
  speakerNumber?: number;
  text: string;
  professional?: SpeakerParty;
  caregiver?: SpeakerParty;
  interim?: boolean;
  animated?: boolean;
  /** First bubble of a consecutive same-speaker group: show avatar + name. */
  showHeader?: boolean;
  /** When the utterance was spoken; shown as HH:mm next to the speaker name. */
  time?: string | Date | null;
  /**
   * Whose viewpoint to render from — "me" sits on the right in primary colour.
   * Defaults to 'professional' (auth-side); the guest/owner view passes
   * 'caregiver' so the owner reads on the right.
   */
  perspective?: 'professional' | 'caregiver';
}

export default function TranscriptChatRow({
  speaker,
  speakerNumber,
  text,
  professional,
  caregiver,
  interim = false,
  animated = false,
  showHeader = true,
  perspective = 'professional',
  time,
}: TranscriptChatRowProps): JSX.Element {
  const theme = useTheme();
  const { t } = useTranslation();
  const style = resolveSpeakerStyle(
    speaker,
    professional,
    caregiver,
    perspective,
  );
  const roleKeys = {
    professional: 'visit.transcriptionRoleProfessional',
    caregiver: 'visit.transcriptionRoleCaregiver',
    unknown: 'visit.transcriptionRoleUnknown',
  };
  const showNumberedLabel = speakerNumber && !style.party?.name?.trim();
  const label = showNumberedLabel
    ? `${t(roleKeys[speaker])} · ${t('visit.transcriptionSpeaker', {
        number: speakerNumber,
      })}`
    : style.label;
  const isRight = style.align === 'flex-end';

  const formattedTime = (() => {
    if (!time) return null;
    const date = new Date(time);
    return Number.isNaN(date.getTime())
      ? null
      : date.toLocaleTimeString('pl-PL', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
  })();

  const avatarNode =
    style.isAssistant && !speakerNumber ? (
      <AssistantAvatar />
    ) : (
      <CustomAvatar
        src={style.party?.avatar || PLACEHOLDER_AVATAR}
        name={style.party?.name}
        alt={label}
        sx={{ width: 36, height: 36, flexShrink: 0 }}
      />
    );
  // Grouped (non-first) bubbles drop the avatar but keep its column width so
  // the bubbles stay aligned under the speaker's avatar.
  const avatar = showHeader ? (
    avatarNode
  ) : (
    <Box sx={{ width: 36, flexShrink: 0 }} />
  );

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'flex-start',
        gap: 1.5,
        alignSelf: style.align,
        maxWidth: '85%',
        flexDirection: isRight ? 'row-reverse' : 'row',
        // Larger gap when a new speaker group starts, tight within a group.
        mt: showHeader ? 2 : 0.5,
        ...(animated && {
          animation: `${appear} ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeOut}`,
          transformOrigin: isRight ? 'right top' : 'left top',
        }),
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {avatar}
      <Stack
        sx={{
          gap: 0.5,
          minWidth: 0,
          alignItems: isRight ? 'flex-end' : 'flex-start',
        }}
      >
        {showHeader && (
          <Typography variant="caption" color="text.secondary">
            {interim ? t('visit.transcriptionLivePreview') : label}
            {formattedTime ? ` · ${formattedTime}` : ''}
          </Typography>
        )}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: style.bubbleBg,
            boxShadow: theme.customShadows.z1,
            ...(interim && {
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.18),
              boxShadow: 'none',
            }),
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              ...(interim && {
                fontStyle: 'italic',
                color: 'text.secondary',
              }),
            }}
          >
            {text}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}
